import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { X, Trash2, Plus, Minus, ShoppingBag, Loader2, FileText } from "lucide-react";
import { useCart, itemKey } from "@/context/CartContext";
import { fmtINR } from "@/data/catalog";

export default function CartDrawer() {
  const { items, open, setOpen, updateQty, removeItem, subtotal, clear } = useCart();
  const navigate = useNavigate();

  const requestQuote = () => {
    setOpen(false);
    navigate("/cart");
  };

  return (
    <AnimatePresence>
      {open && (
        <>
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} onClick={() => setOpen(false)} className="fixed inset-0 z-[70] bg-black/50 backdrop-blur-sm" data-testid="cart-overlay" />
          <motion.aside
            initial={{ x: "100%" }} animate={{ x: 0 }} exit={{ x: "100%" }} transition={{ type: "tween", ease: [0.22, 1, 0.36, 1], duration: 0.4 }}
            className="fixed right-0 top-0 z-[80] h-full w-full max-w-md bg-card border-l border-border flex flex-col" data-testid="cart-drawer"
          >
            <div className="flex items-center justify-between p-5 border-b border-border">
              <h3 className="font-heading text-lg font-bold flex items-center gap-2"><ShoppingBag size={18} /> Your Cart</h3>
              <button onClick={() => setOpen(false)} data-testid="cart-close" className="grid place-items-center h-9 w-9 rounded-full hover:bg-secondary"><X size={18} /></button>
            </div>

            {items.length === 0 ? (
              <div className="flex-1 grid place-items-center text-center px-8">
                <div>
                  <ShoppingBag size={40} className="mx-auto text-muted-foreground" />
                  <p className="mt-4 text-muted-foreground">Your cart is empty.</p>
                  <Link to="/products" onClick={() => setOpen(false)} className="mt-4 inline-block rounded-full bg-navy text-white px-6 py-2.5 text-sm font-semibold">Browse products</Link>
                </div>
              </div>
            ) : (
              <>
                <div className="flex-1 overflow-y-auto p-5 space-y-4">
                  {items.map((i) => {
                    const k = itemKey(i);
                    return (
                      <div key={k} className="flex gap-3" data-testid={`cart-item-${i.slug}`}>
                        <img src={i.image} alt={i.name} className="h-20 w-20 rounded-xl object-cover border border-border" />
                        <div className="flex-1 min-w-0">
                          <p className="font-semibold text-foreground text-sm line-clamp-1">{i.name}</p>
                          <p className="text-xs text-muted-foreground">{[i.size, i.color].filter(Boolean).join(" · ")}</p>
                          <p className="text-sm font-mono-accent text-royal mt-0.5">{fmtINR(i.price)}</p>
                          <div className="mt-2 flex items-center gap-3">
                            <div className="flex items-center border border-border rounded-full">
                              <button onClick={() => updateQty(k, i.quantity - 1)} className="grid place-items-center h-7 w-7" data-testid={`cart-dec-${i.slug}`}><Minus size={13} /></button>
                              <span className="w-8 text-center text-sm">{i.quantity}</span>
                              <button onClick={() => updateQty(k, i.quantity + 1)} className="grid place-items-center h-7 w-7" data-testid={`cart-inc-${i.slug}`}><Plus size={13} /></button>
                            </div>
                            <button onClick={() => removeItem(k)} className="text-muted-foreground hover:text-destructive" data-testid={`cart-remove-${i.slug}`}><Trash2 size={15} /></button>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
                <div className="p-5 border-t border-border space-y-3">
                  <div className="flex justify-between text-sm"><span className="text-muted-foreground">Subtotal (est.)</span><span className="font-mono-accent font-semibold text-foreground" data-testid="cart-subtotal">{fmtINR(subtotal)}</span></div>
                  <button onClick={requestQuote} data-testid="cart-quote" className="w-full inline-flex items-center justify-center gap-2 rounded-full bg-navy text-white px-6 py-3.5 font-semibold hover:bg-royal transition-colors">
                    <FileText size={17} /> Request Bulk Quote
                  </button>
                  <button onClick={clear} className="w-full text-xs text-muted-foreground hover:text-destructive" data-testid="cart-clear">Clear cart</button>
                </div>
              </>
            )}
          </motion.aside>
        </>
      )}
    </AnimatePresence>
  );
}
